<template>
  <div :class="$style.container">
    <HgToast
      :isVisible="showToast"
      position="top-center"
      :width="notificationWidth"
      animationName="slide"
      :keepVisible="disableAutoHideToast"
      @clear="clearToast"
    >
      <template v-for="(toast, key) in ToastMessages" :key="key">
        <HgNotificationMessage
          v-if="toastType === key"
          showCloseButton
          :theme="toast.theme"
          :title="toast.title ? t(toast.title) : ''"
          :messageClass="$style.notificationWrapper"
          :class="{ [$style.icon]: !toast.title }"
          @close="clearToast"
        >
          <template v-if="toast.icon" #icon>
            <HgIcon :icon="toast.icon" :theme="toast.iconTheme" />
          </template>
          <i18n-t :keypath="toast.message" tag="p">
            <template v-if="toast.action && toast.link" #action>
              <HgLink :href="t(toast.link)" target="_blank">{{ t(toast.action) }}</HgLink>
            </template>
          </i18n-t>
        </HgNotificationMessage>
      </template>
    </HgToast>
  </div>
</template>

<script setup lang="ts">
import {
  HgIcon,
  HgLink,
  HgNotificationMessage,
  HgToast,
  Icon,
  IconTheme,
  NotificationMessageTheme,
} from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { ToastType } from '@/types';

type ToastMessagesType = {
  [K in Exclude<ToastType, ToastType.None>]: {
    theme: NotificationMessageTheme;
    title?: string;
    message: string;
    icon?: Icon;
    iconTheme?: IconTheme;
    disableAutoHide?: boolean;
    action?: string;
    link?: string;
  };
};

interface Props {
  notificationWidth?: string;
}

const props = withDefaults(defineProps<Props>(), {
  notificationWidth: '380px',
});

const { t } = useI18n();
const propertyDetailsStore = usePropertyDetailsStore();

const toastType = computed(() => propertyDetailsStore.toastType);

const showToast = computed(() => toastType.value !== ToastType.None);

const ToastMessages: ToastMessagesType = {
  [ToastType.AddSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'propertyDetail.myProperty.propertyAddedSuccessMessage.message',
    icon: Icon.Success,
    iconTheme: IconTheme.Success,
  },
  [ToastType.EditSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'valuationForm.edit.toast.body',
    icon: Icon.Success,
    iconTheme: IconTheme.Success,
  },
  [ToastType.SubmitSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'propertyValuation.submitSuccess.message',
    icon: Icon.Success,
    iconTheme: IconTheme.Success,
  },
  [ToastType.RemoveSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'overview.myProperty.removePropertySuccess.toastMessage',
    icon: Icon.Success,
    iconTheme: IconTheme.Success,
  },
  [ToastType.RemoveFailure]: {
    theme: NotificationMessageTheme.Danger,
    message: 'overview.removePropertyFail.toastMessage',
    icon: Icon.Error,
    iconTheme: IconTheme.Danger,
  },
  [ToastType.AddFailure]: {
    theme: NotificationMessageTheme.Danger,
    message: 'overview.addPropertyFail.toastMessage',
    icon: Icon.Error,
    iconTheme: IconTheme.Danger,
  },
  [ToastType.PrefilledData]: {
    theme: NotificationMessageTheme.Info,
    message: 'iaziAddressPrefilled.notificationMessage',
  },
  [ToastType.PropertyTypeChangedApartment]: {
    theme: NotificationMessageTheme.Info,
    title: 'propertyTypeChange.notificationTitle.apartment',
    message: 'propertyTypeChange.notificationMessage.apartment',
  },
  [ToastType.OtpSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'smsConfirmation.notification.description',
  },
  [ToastType.OtpLimitReached]: {
    theme: NotificationMessageTheme.Info,
    title: 'smsConfirmation.hint.title',
    message: 'smsConfirmation.hint.description',
    disableAutoHide: true,
    action: 'smsConfirmation.hint.action',
    link: 'smsConfirmation.hint.actionLink',
  },
  [ToastType.LocationSuccess]: {
    theme: NotificationMessageTheme.Success,
    message: 'location.addressFound.message',
    icon: Icon.Success,
    iconTheme: IconTheme.Success,
  },
  [ToastType.TooManyRequests]: {
    theme: NotificationMessageTheme.Danger,
    title: 'propertyValuation.tooManyRequest.toastTitle',
    message: 'propertyValuation.tooManyRequest.toastMessage',
    icon: Icon.Error,
    iconTheme: IconTheme.Danger,
  },
  [ToastType.ServerFailure]: {
    theme: NotificationMessageTheme.Danger,
    title: 'propertyValuation.serverFailure.toastTitle',
    message: 'propertyValuation.serverFailure.toastMessage',
    icon: undefined,
    iconTheme: undefined,
  },
  [ToastType.AgentSelectionRequired]: {
    theme: NotificationMessageTheme.Danger,
    title: 'agentRecommendation.hint',
    message: 'agentRecommendation.noBrokerSelection',
    icon: Icon.Error,
    iconTheme: IconTheme.Danger,
  },
};

const disableAutoHideToast = computed(() => {
  return ToastMessages[toastType.value]?.disableAutoHide ?? false;
});

const clearToast = () => {
  propertyDetailsStore.setToastType(ToastType.None);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  [class*='HgToast_topCenter'] {
    margin-top: $space-500;
    max-width: 100%;

    .notificationWrapper {
      p {
        max-width: 100%;
        white-space: wrap;
      }
    }
  }
}

.icon {
  [class*='NotificationMessage_iconSide'] {
    align-content: center;
  }
}
</style>
